import React, { FunctionComponent } from "react";

import {
  DefaultButton,
  Spinner,
  SpinnerSize,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { useCanUnlinkTenant } from "@libs/api/gateways/plt/plt-gateway.hooks";

interface TenantUnlinkButtonsProps {
  tenant: Tenant;
  onTransferClick: (tenant: Tenant) => void;
  onUnlinkClick: (tenant: Tenant) => void;
  isTransferMode: boolean;
  isSource: boolean;
  isTarget: boolean;
  canTransfer: boolean;
  onSelectTarget: () => void;
  onCancelTransfer: () => void;
}

export const TenantUnlinkButtons: FunctionComponent<TenantUnlinkButtonsProps> = ({
  tenant,
  onTransferClick,
  onUnlinkClick,
  isTransferMode,
  isSource,
  isTarget,
  canTransfer,
  onSelectTarget,
  onCancelTransfer
}) => {
  const theme = useTheme();
  const apiArgs = {
    tenantId: tenant.customerTenantId ?? "",
    childTenantId: tenant.id
  };

  const { data, isLoading } = useCanUnlinkTenant(apiArgs);
  if (isLoading) return <Spinner size={SpinnerSize.small} />;
  if (!data) return null;

  const hasLicencesOrSubs = data.licenseCount > 0 || data.subscriptionCount > 0;

  if (isTransferMode) {
    if (isSource) {
      return (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DefaultButton onClick={onCancelTransfer} primary>
            Cancel transfer
          </DefaultButton>
        </Stack>
      );
    }
    if (isTarget) {
      return (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DefaultButton
            iconProps={{
              iconName: "ReturnToSession",
              styles: {
                root: {
                  color: theme.semanticColors.actionLink
                }
              }
            }}
            onClick={onSelectTarget}
          >
            Transfer to this tenant
          </DefaultButton>
        </Stack>
      );
    }
  }

  return (
    <Stack horizontal tokens={{ childrenGap: 8 }}>
      <DefaultButton onClick={() => onUnlinkClick(tenant)}>
        Unlink
      </DefaultButton>
      {hasLicencesOrSubs && canTransfer && (
        <DefaultButton onClick={() => onTransferClick(tenant)}>
          Transfer licences
        </DefaultButton>
      )}
    </Stack>
  );
};
